import { Box } from "@mui/material";
import { useMemo, useCallback, memo } from "react";
import { useMainContext } from "../../../../../context/useMainContext";

function HistoryCell({ row }) {
  const { availableUsers } = useMainContext();

  const getUserName = useCallback(
    (userId) => {
      if (!userId) return "Unknown";
      const user = availableUsers.find((u) => u.id === userId);
      return user ? user.name : userId;
    },
    [availableUsers]
  );

  const latestStatusChange = useMemo(() => {
    return row.status_history?.length > 0
      ? [...row.status_history].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )[0]
      : null;
  }, [row.status_history]);

  const latestFreeCheck = useMemo(() => {
    return row.free_check_history?.length > 0
      ? [...row.free_check_history].sort((a, b) => {
          const dateA = new Date(a.timestamp || a.created_at);
          const dateB = new Date(b.timestamp || b.created_at);
          return dateB - dateA;
        })[0]
      : null;
  }, [row.free_check_history]);

  const latestPaidCheck = useMemo(() => {
    return row.paid_check_history?.length > 0
      ? [...row.paid_check_history].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        )[0]
      : null;
  }, [row.paid_check_history]);

  const mostRecent = useMemo(() => {
    const allDates = [
      latestStatusChange
        ? {
            date: latestStatusChange.created_at,
            source: "Status",
            data: latestStatusChange,
            changedBy: getUserName(latestStatusChange.assigned_member),
          }
        : null,
      latestFreeCheck
        ? {
            date: latestFreeCheck.timestamp || latestFreeCheck.created_at,
            source: "Free Check",
            data: latestFreeCheck,
            changedBy: getUserName(latestFreeCheck.checked_by),
          }
        : null,
      latestPaidCheck
        ? {
            date: latestPaidCheck.timestamp,
            source: "Paid Check",
            data: latestPaidCheck,
            changedBy: getUserName(latestPaidCheck.checked_by),
          }
        : null,
    ].filter(Boolean);

    return allDates.length > 0
      ? allDates.sort((a, b) => new Date(b.date) - new Date(a.date))[0]
      : null;
  }, [latestStatusChange, latestFreeCheck, latestPaidCheck, getUserName]);

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, ".");
  }, []);

  if (!mostRecent) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        No history available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      Last refreshed: {formatDate(mostRecent.date)}
      <Box> | </Box>
      Changed by: {mostRecent.changedBy}
      <Box> | </Box>
      Source: {mostRecent.source}
    </Box>
  );
}

export default memo(HistoryCell);
